import React, { useState, useEffect } from 'react';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { ModalDialogProvider } from './context/ModalDialogContext';
import Header from './components/Header';
import LandingLayout from './components/LandingLayout';
import AuthLayout from './components/AuthLayout';
import DashboardLayout from './components/DashboardLayout';
import Footer from './components/Footer';
import MobileCoinCardView from './components/MobileCoinCardView';
import DemoLodgeSite from './components/DemoLodgeSite';
import DemoBookingSite from './components/DemoBookingSite';
import VideoTutorialPage from './components/VideoTutorialPage';
import { useIsMobileView } from './hooks/useIsMobileView';
import MobileAuthScreen from './components/mobile/MobileAuthScreen';
import MobileAppLayout from './components/mobile/MobileAppLayout';
import SupportPage from './components/SupportPage';

// Global fetch interceptor to catch 401 Unauthorized errors and trigger auto-logout
const originalFetch = window.fetch;
window.fetch = async function (...args) {
  try {
    const response = await originalFetch(...args);
    if (response?.status === 401) {
      const url = typeof args[0] === 'string' ? args[0] : args[0]?.url;
      if (
        url &&
        !url.includes('/auth/login') &&
        !url.includes('/auth/signup') &&
        !url.includes('/ai-designer/') &&
        !url.includes('/api/upload/resample') &&
        !url.includes('/api/upload/optimize')
      ) {
        window.dispatchEvent(new Event('unauthorized'));
      }
    }
    return response;
  } catch (err) {
    throw err;
  }
};

function MainApp() {
  const { loading: themeLoading, tenantConfig } = useTheme();
  const { isMobile, isForcedDesktop, switchToDesktop, switchToMobile } = useIsMobileView();
  const [view, setView] = useState(() => {
    try {
      const path = (window.location.pathname || '').toLowerCase().replace(/\/$/, '');
      const params = new URLSearchParams(window.location.search || '');
      if (['/register', '/signup', '/login', '/auth', '/apply-agent'].includes(path) || params.has('invite') || params.has('ref')) {
        return 'auth';
      }
    } catch (_) {}
    return 'landing';
  }); // views: 'landing' or 'auth'
  const [user, setUser] = useState(null); // stores logged-in user payload
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const path = (window.location.pathname || '').toLowerCase().replace(/\/$/, '');
      const hash = (window.location.hash || '').toLowerCase().replace(/^#/, '');
      const params = new URLSearchParams(window.location.search || '');
      const tabParam = (params.get('tab') || '').toLowerCase();
      const target = (path.startsWith('/') ? path.slice(1) : path) || hash || tabParam;

      if (['settings', 'site-settings', 'styling', 'customizer'].includes(target)) return 'settings';
      if (['vault', 'brag', 'cards'].includes(target)) return 'vault';
      if (['mint', 'quick-mint'].includes(target)) return 'mint';
      if (['production', 'orders'].includes(target)) return 'production';
      if (['users'].includes(target)) return 'users';
      if (['companies', 'organisations', 'organisation'].includes(target)) return 'companies';
      if (['agent_network', 'agents', 'agent-network'].includes(target)) return 'agent_network';
      if (['data_manager', 'data'].includes(target)) return 'data_manager';
      if (['support', 'help', 'rfid'].includes(target)) return 'support';
      if (['profile'].includes(target)) return 'profile';
    } catch (_) {}
    return 'overview';
  }); // lifted dashboard tab state
  const [sessionLoading, setSessionLoading] = useState(true);

  // Synchronize browser URL with active dashboard tab
  useEffect(() => {
    if (user && view === 'landing') {
      try {
        const path = (window.location.pathname || '').toLowerCase().replace(/\/$/, '') || '/';
        const targetPath = activeTab === 'overview' ? '/' : `/${activeTab}`;
        if (path !== targetPath && !['/demosite', '/demobook', '/tutorial', '/guide'].includes(path) && !path.startsWith('/c/')) {
          window.history.replaceState(null, '', targetPath);
        }
      } catch (_) {}
    }
  }, [activeTab, user, view]);

  useEffect(() => {
    const handleUnauthorized = () => {
      console.warn("[Auth] Session expired or unauthorized. Logging out.");
      sessionStorage.removeItem('token');
      localStorage.removeItem('token');
      setUser(null);
      setView('auth');
    };

    window.addEventListener('unauthorized', handleUnauthorized);
    return () => {
      window.removeEventListener('unauthorized', handleUnauthorized);
    };
  }, []);

  useEffect(() => {
    const token = sessionStorage.getItem('token') || localStorage.getItem('token');
    if (!token) {
      setSessionLoading(false);
      return;
    }

    const isDev = window.location.port === '3000';
    const API_BASE = isDev ? `http://${window.location.hostname}:8000` : '';
    fetch(`${API_BASE}/api/v1/users/me`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'X-Tenant-Domain': window.location.hostname
      }
    })
      .then(res => {
        if (!res.ok) throw new Error("Invalid session token");
        return res.json();
      })
      .then(data => {
        setUser(data);
        setSessionLoading(false);
      })
      .catch(err => {
        console.warn("[SessionRestore] Token validation failed. Clearing local storage.", err);
        sessionStorage.removeItem('token');
        localStorage.removeItem('token');
        setUser(null);
        setSessionLoading(false);
      });
  }, []);

  const loading = themeLoading || sessionLoading;

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: 'var(--bg-color)',
        color: 'var(--text-mute)',
        fontFamily: 'sans-serif'
      }}>
        <h2>Loading tenant context...</h2>
      </div>
    );
  }

  // Mobile experience specifically for mobile browsers
  if (isMobile) {
    if (!user) {
      return (
        <MobileAuthScreen
          setUser={(loggedInUser) => {
            setUser(loggedInUser);
            setView('landing');
          }}
          onSwitchToDesktop={switchToDesktop}
        />
      );
    }

    return (
      <MobileAppLayout
        user={user}
        tenant={tenantConfig}
        onUserUpdated={(updatedUser) => setUser(updatedUser)}
        onSwitchToDesktop={switchToDesktop}
        onLogout={() => {
          sessionStorage.removeItem('token');
          localStorage.removeItem('token');
          setUser(null);
          setView('auth');
        }}
      />
    );
  }

  const isDashboardView = view === 'landing' && user;

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: isDashboardView ? '100vh' : undefined,
      minHeight: '100vh',
      maxHeight: isDashboardView ? '100vh' : undefined,
      overflow: isDashboardView ? 'hidden' : 'visible',
      background: 'var(--panel-bg, var(--bg-color))',
      color: 'var(--text-main)',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      <Header 
        setView={setView} 
        user={user} 
        setUser={setUser} 
        activeTab={activeTab} 
        setActiveTab={setActiveTab} 
      />
      {view === 'landing' ? (
        user ? (
          <DashboardLayout 
            setView={setView} 
            user={user} 
            setUser={setUser} 
            activeTab={activeTab} 
            setActiveTab={setActiveTab} 
          />
        ) : activeTab === 'support' ? (
          <div style={{ flex: 1, padding: '24px 20px', background: 'var(--bg-color)' }}>
            <SupportPage
              user={null}
              tenantConfig={tenantConfig}
              setActiveTab={setActiveTab}
              setView={setView}
            />
          </div>
        ) : (
          <LandingLayout setView={setView} user={user} />
        )
      ) : (
        <AuthLayout setView={setView} setUser={setUser} />
      )}
      {!isDashboardView && <Footer />}

      {/* Floating return pill if user is on mobile but requested desktop mode */}
      {isForcedDesktop && (
        <button
          onClick={switchToMobile}
          style={{
            position: 'fixed',
            bottom: '18px',
            right: '18px',
            zIndex: 9999,
            backgroundColor: '#0f172a',
            color: '#f8fafc',
            border: '1px solid #334155',
            borderRadius: '24px',
            padding: '10px 18px',
            fontSize: '12px',
            fontWeight: 700,
            cursor: 'pointer',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            backdropFilter: 'blur(8px)'
          }}
        >
          <span>📱</span>
          <span>Back to Mobile App</span>
        </button>
      )}
    </div>
  );
}

export default function App() {
  const pathname = (window.location.pathname || '').toLowerCase().replace(/\/$/, '') || '/';
  const coinMatch = window.location.pathname.match(/^\/c\/([a-zA-Z0-9_-]+)/);
  if (coinMatch) {
    return <MobileCoinCardView publicCode={coinMatch[1]} />;
  }

  if (pathname === '/demosite') {
    return <DemoLodgeSite />;
  }

  if (pathname === '/demobook') {
    return <DemoBookingSite />;
  }

  if (pathname === '/tutorial' || pathname === '/guide' || pathname === '/how-to' || pathname.startsWith('/tutorial/')) {
    return (
      <ThemeProvider>
        <ModalDialogProvider>
          <VideoTutorialPage />
        </ModalDialogProvider>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
      <ModalDialogProvider>
        <MainApp />
      </ModalDialogProvider>
    </ThemeProvider>
  );
}
